import { supabase } from '../../../config/supabase';
import {
  ASSESSMENT_V2_COMPOSITE_REPORT_KEY,
  ASSESSMENT_V2_ENABLED_TESTS,
  ASSESSMENT_V2_REPORT_KEY,
  isAssessmentV2ReportKey,
} from '../registry/assessmentRegistry';
import type { AssessmentResultLifecycleAction, AssessmentResultLifecycleEntry, AssessmentResultV2 } from '../types';

export interface AssessmentTestUsage {
  testKey: string;
  usageCount: number;
  lastUsedAt: string | null;
}

export interface CompletedAssessmentsForPatientOptions {
  page?: number;
  pageSize?: number;
  dateFrom?: string;
  dateTo?: string;
  testKeys?: readonly string[];
  professionalUserId?: string;
}

export interface AssessmentResultsDashboardOptions {
  patientId?: string | null;
  matchingPatientIds?: readonly string[];
  page?: number;
  pageSize?: 50 | 100 | 200;
  search?: string;
  status?: 'active' | 'cancelled' | 'all';
}

export type AssessmentResultV2UpdatePayload = Partial<
  Pick<
    AssessmentResultV2,
    | 'assessmentDate'
    | 'notes'
    | 'summaryJson'
    | 'payloadJson'
    | 'assetsJson'
    | 'patientNameSnapshot'
    | 'patientMedicalRecordSnapshot'
    | 'patientBirthDateSnapshot'
    | 'patientSexSnapshot'
    | 'patientWeightSnapshot'
    | 'patientHeightSnapshot'
    | 'professionalUserId'
    | 'professionalNameSnapshot'
    | 'professionalRegistrationSnapshot'
    | 'professionalRoleSnapshot'
  >
>;

export interface AssessmentResultV2Revision {
  updatedAt?: string | null;
  createdAt?: string | null;
}

export class AssessmentResultsV2Service {
  private static readonly DEFAULT_COMPLETED_PAGE_SIZE = 20;
  private static readonly MAX_COMPLETED_PAGE_SIZE = 100;
  private static readonly DEFAULT_DASHBOARD_PAGE_SIZE = 50;
  private static readonly DASHBOARD_PAGE_SIZES = [50, 100, 200] as const;
  private static readonly DASHBOARD_SEARCH_FIELDS = [
    'patientNameSnapshot',
    'patientMedicalRecordSnapshot',
    'professionalNameSnapshot',
    'professionalRegistrationSnapshot',
    'notes',
    'testKey',
    'summaryJson->>classification',
    'summaryJson->>severity',
    'summaryJson->>normativeBandLabel',
    'summaryJson->>title',
    'summaryJson->>total',
    'summaryJson->>grade',
    'summaryJson->>percentage',
    'summaryJson->>percentPredicted',
    'summaryJson->>bestDistance_m',
    'summaryJson->>timeSeconds',
    'summaryJson->>speedMs',
    'summaryJson->>bestOverallKg',
    'summaryJson->>totalSteps',
    'summaryJson->>overallScore',
  ] as const;
  private static readonly MAX_IDS_PER_QUERY = 50;

  private static getDashboardSearchTokens(search?: string): string[] {
    if (!search?.trim()) return [];

    return search
      .normalize('NFKC')
      .split(/[^\p{L}\p{N}]+/u)
      .map((token) => token.trim())
      .filter(Boolean)
      .slice(0, 6);
  }

  private static mapRecord(item: AssessmentResultV2): AssessmentResultV2 {
    return {
      ...item,
      status: item.status ?? 'completed',
      testVersion: item.testVersion ?? 1,
      groupIndex: item.groupIndex ?? 1,
      groupTotal: item.groupTotal ?? 1,
      summaryJson: item.summaryJson && typeof item.summaryJson === 'object' ? item.summaryJson : {},
      payloadJson: item.payloadJson && typeof item.payloadJson === 'object' ? item.payloadJson : {},
      assetsJson: item.assetsJson && typeof item.assetsJson === 'object' ? item.assetsJson : {},
    };
  }

  static async countByClientId(clientId: string): Promise<number> {
    const { count, error } = await supabase
      .from('assessment_results_v2')
      .select('resultId', { count: 'exact', head: true })
      .eq('clientId', clientId);

    if (error) {
      console.error('Erro ao contar assessment_results_v2:', error);
      throw error;
    }

    return count ?? 0;
  }

  static async listRecentByClientId(clientId: string, limit = 10): Promise<AssessmentResultV2[]> {
    const { data, error } = await supabase
      .from('assessment_results_v2')
      .select('*')
      .eq('clientId', clientId)
      .order('assessmentDate', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Erro ao listar assessment_results_v2:', error);
      throw error;
    }

    return ((data as AssessmentResultV2[] | null) ?? []).map((item) => this.mapRecord(item));
  }

  static async listPageForDashboard(
    clientId: string,
    options: AssessmentResultsDashboardOptions = {},
  ): Promise<{ items: AssessmentResultV2[]; total: number }> {
    const page = options.page ?? 1;
    const pageSize = options.pageSize ?? this.DEFAULT_DASHBOARD_PAGE_SIZE;
    if (!Number.isSafeInteger(page) || page < 1) {
      throw new RangeError('page deve ser um inteiro maior ou igual a 1.');
    }
    if (!this.DASHBOARD_PAGE_SIZES.includes(pageSize)) {
      throw new RangeError('pageSize deve ser 50, 100 ou 200.');
    }

    const rangeFrom = (page - 1) * pageSize;
    const rangeTo = rangeFrom + pageSize - 1;
    if (!Number.isSafeInteger(rangeFrom) || !Number.isSafeInteger(rangeTo)) {
      throw new RangeError('A combinação de page e pageSize excede o intervalo seguro.');
    }

    const patientId = options.patientId?.trim();
    const displayableTestKeys = [
      ...ASSESSMENT_V2_ENABLED_TESTS,
      ASSESSMENT_V2_REPORT_KEY,
      ASSESSMENT_V2_COMPOSITE_REPORT_KEY,
    ];
    let query = supabase
      .from('assessment_results_v2')
      .select('*', { count: 'exact' })
      .eq('clientId', clientId)
      .in('testKey', displayableTestKeys);

    if (patientId) {
      query = query.eq('patientId', patientId);
    }

    if (options.status === 'cancelled') {
      query = query.eq('status', 'cancelled');
    } else if (options.status === 'all') {
      query = query.in('status', ['completed', 'cancelled']);
    } else {
      query = query.eq('status', 'completed');
    }

    const searchTokenFilters = this.getDashboardSearchTokens(options.search).map((token) =>
      this.DASHBOARD_SEARCH_FIELDS
        .map((field) => `${field}.ilike.*${token}*`)
        .join(','),
    );
    const matchingPatientIds = [...new Set(options.matchingPatientIds ?? [])]
      .map((id) => id.trim())
      .filter((id) => /^[-A-Za-z0-9_]+$/.test(id))
      .slice(0, this.MAX_IDS_PER_QUERY);
    if (searchTokenFilters.length > 0) {
      const textSearchFilter = searchTokenFilters.length === 1
        ? searchTokenFilters[0]
        : `and(${searchTokenFilters.map((filter) => `or(${filter})`).join(',')})`;
      const patientSearchFilter = matchingPatientIds.length > 0
        ? `patientId.in.(${matchingPatientIds.join(',')}),`
        : '';
      query = query.or(`${patientSearchFilter}${textSearchFilter}`);
    }

    const { data, count, error } = await query
      .order('assessmentDate', { ascending: false })
      .range(rangeFrom, rangeTo);

    if (error) {
      console.error('Erro ao listar página de assessment_results_v2:', error);
      throw error;
    }

    return {
      items: ((data as AssessmentResultV2[] | null) ?? []).map((item) => this.mapRecord(item)),
      total: count ?? 0,
    };
  }

  static async listTestUsageByClientId(
    clientId: string,
    options?: { since?: string; limit?: number; testKeys?: string[] },
  ): Promise<AssessmentTestUsage[]> {
    const { data, error } = await supabase.rpc('get_assessment_test_usage_by_client', {
      p_client_id: clientId,
      p_since: options?.since ?? null,
      p_limit: options?.limit ?? 7,
      p_test_keys: options?.testKeys ?? null,
    });

    if (error) {
      console.error('Erro ao listar uso de testes por tenant:', error);
      throw error;
    }

    return ((data as Array<Record<string, unknown>> | null) ?? [])
      .map((item) => ({
        testKey: typeof item.testKey === 'string' ? item.testKey : '',
        usageCount: Number(item.usageCount ?? 0),
        lastUsedAt: typeof item.lastUsedAt === 'string' ? item.lastUsedAt : null,
      }))
      .filter((item) => item.testKey.length > 0 && Number.isFinite(item.usageCount) && item.usageCount > 0);
  }

  static async listByClientAndTest(clientId: string, testKey: string, limit = 20): Promise<AssessmentResultV2[]> {
    const { data, error } = await supabase
      .from('assessment_results_v2')
      .select('*')
      .eq('clientId', clientId)
      .eq('testKey', testKey)
      .order('assessmentDate', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Erro ao listar assessment_results_v2 por teste:', error);
      throw error;
    }

    return ((data as AssessmentResultV2[] | null) ?? []).map((item) => this.mapRecord(item));
  }

  static async listCompletedForPatient(
    clientId: string,
    patientId: string,
    options: CompletedAssessmentsForPatientOptions = {},
  ): Promise<{ items: AssessmentResultV2[]; total: number }> {
    const page = options.page ?? 1;
    const pageSize = options.pageSize ?? this.DEFAULT_COMPLETED_PAGE_SIZE;

    if (!Number.isSafeInteger(page) || page < 1) {
      throw new RangeError('page deve ser um inteiro maior ou igual a 1.');
    }

    if (!Number.isSafeInteger(pageSize) || pageSize < 1 || pageSize > this.MAX_COMPLETED_PAGE_SIZE) {
      throw new RangeError(`pageSize deve ser um inteiro entre 1 e ${this.MAX_COMPLETED_PAGE_SIZE}.`);
    }

    const rangeFrom = (page - 1) * pageSize;
    const rangeTo = rangeFrom + pageSize - 1;
    if (!Number.isSafeInteger(rangeFrom) || !Number.isSafeInteger(rangeTo)) {
      throw new RangeError('A combinação de page e pageSize excede o intervalo seguro.');
    }

    const requestedTestKeys =
      options.testKeys === undefined
        ? null
        : [...new Set(options.testKeys.map((testKey) => testKey.trim()))].filter(
            (testKey) => testKey.length > 0 && !isAssessmentV2ReportKey(testKey),
          );

    if (requestedTestKeys?.length === 0) {
      return { items: [], total: 0 };
    }

    let query = supabase
      .from('assessment_results_v2')
      .select('*', { count: 'exact' })
      .eq('clientId', clientId)
      .eq('patientId', patientId)
      .eq('status', 'completed');

    if (options.dateFrom) {
      query = query.gte('assessmentDate', options.dateFrom);
    }

    if (options.dateTo) {
      query = query.lte('assessmentDate', options.dateTo);
    }

    if (options.professionalUserId) {
      query = query.eq('professionalUserId', options.professionalUserId);
    }

    if (requestedTestKeys) {
      query = query.in('testKey', requestedTestKeys);
    } else {
      query = query
        .neq('testKey', ASSESSMENT_V2_REPORT_KEY)
        .neq('testKey', ASSESSMENT_V2_COMPOSITE_REPORT_KEY);
    }

    const { data, count, error } = await query
      .order('assessmentDate', { ascending: false })
      .range(rangeFrom, rangeTo);

    if (error) {
      console.error('Erro ao listar avaliações concluídas do paciente:', error);
      throw error;
    }

    return {
      items: ((data as AssessmentResultV2[] | null) ?? []).map((item) => this.mapRecord(item)),
      total: count ?? 0,
    };
  }

  static async listBySessionId(clientId: string, sessionId: string): Promise<AssessmentResultV2[]> {
    const { data, error } = await supabase
      .from('assessment_results_v2')
      .select('*')
      .eq('clientId', clientId)
      .eq('sessionId', sessionId)
      .order('groupIndex', { ascending: true });

    if (error) {
      console.error('Erro ao listar assessment_results_v2 por sessão:', error);
      throw error;
    }

    return ((data as AssessmentResultV2[] | null) ?? []).map((item) => this.mapRecord(item));
  }

  static async listHistoryForPatient(
    clientId: string,
    patientId: string,
    testKey: string,
    options?: { excludeResultId?: string; limit?: number; beforeAssessmentDate?: string },
  ): Promise<AssessmentResultV2[]> {
    let query = supabase
      .from('assessment_results_v2')
      .select('*')
      .eq('clientId', clientId)
      .eq('patientId', patientId)
      .eq('testKey', testKey)
      .neq('status', 'cancelled')
      .order('assessmentDate', { ascending: false })
      .limit(options?.limit ?? 6);

    if (options?.excludeResultId) {
      query = query.neq('resultId', options.excludeResultId);
    }
    if (options?.beforeAssessmentDate) {
      query = query.lt('assessmentDate', options.beforeAssessmentDate).eq('status', 'completed');
    }

    const { data, error } = await query;

    if (error) {
      console.error('Erro ao listar historico de assessment_results_v2:', error);
      throw error;
    }

    return ((data as AssessmentResultV2[] | null) ?? []).map((item) => this.mapRecord(item));
  }

  static async create(
    payload: Omit<AssessmentResultV2, 'resultId' | 'createdAt' | 'updatedAt'>,
  ): Promise<AssessmentResultV2> {
    const { data, error } = await supabase
      .from('assessment_results_v2')
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      console.error('Erro ao criar assessment_results_v2:', error);
      throw error;
    }

    return this.mapRecord(data as AssessmentResultV2);
  }

  static async upsert(
    payload: Omit<AssessmentResultV2, 'resultId' | 'createdAt' | 'updatedAt'>,
  ): Promise<AssessmentResultV2> {
    const { data, error } = await supabase
      .from('assessment_results_v2')
      .upsert(
        {
          ...payload,
          updatedAt: new Date().toISOString(),
        },
        { onConflict: 'sessionId,testKey' },
      )
      .select('*')
      .single();

    if (error) {
      console.error('Erro ao fazer upsert em assessment_results_v2:', error);
      throw error;
    }

    return this.mapRecord(data as AssessmentResultV2);
  }

  static async update(
    resultId: string,
    clientId: string,
    payload: AssessmentResultV2UpdatePayload,
  ): Promise<AssessmentResultV2> {
    const { data, error } = await supabase
      .from('assessment_results_v2')
      .update({
        ...payload,
        updatedAt: new Date().toISOString(),
      })
      .eq('resultId', resultId)
      .eq('clientId', clientId)
      .select('*')
      .single();

    if (error) {
      console.error('Erro ao atualizar assessment_results_v2:', error);
      throw error;
    }

    return this.mapRecord(data as AssessmentResultV2);
  }

  /**
   * Atualização otimista usada em operações irreversíveis ou autorais.
   *
   * A gravação só ocorre se a revisão lida pelo chamador ainda for a revisão
   * atual. Isso impede que edição e assinatura concorrentes sobrescrevam ou
   * assinem conteúdos diferentes do snapshot efetivamente revisado.
   */
  static async updateIfRevisionMatches(
    resultId: string,
    clientId: string,
    expectedRevision: AssessmentResultV2Revision,
    payload: AssessmentResultV2UpdatePayload,
  ): Promise<AssessmentResultV2> {
    const expectedUpdatedAt = expectedRevision.updatedAt?.trim() || null;
    const expectedCreatedAt = expectedRevision.createdAt?.trim() || null;
    const expectedTimestamp = expectedUpdatedAt ?? expectedCreatedAt;
    if (!expectedTimestamp) {
      throw new Error('ASSESSMENT_RESULT_REVISION_REQUIRED');
    }

    const parsedExpectedTimestamp = Date.parse(expectedTimestamp);
    const nextRevisionTimestamp = new Date(
      Number.isFinite(parsedExpectedTimestamp)
        ? Math.max(Date.now(), parsedExpectedTimestamp + 1)
        : Date.now(),
    ).toISOString();

    let query = supabase
      .from('assessment_results_v2')
      .update({
        ...payload,
        updatedAt: nextRevisionTimestamp,
      })
      .eq('resultId', resultId)
      .eq('clientId', clientId);

    if (expectedUpdatedAt) {
      query = query.eq('updatedAt', expectedUpdatedAt);
    } else {
      query = query.is('updatedAt', null);
      if (expectedCreatedAt) {
        query = query.eq('createdAt', expectedCreatedAt);
      }
    }

    const { data, error } = await query.select('*').maybeSingle();

    if (error) {
      console.error('Erro ao atualizar assessment_results_v2 com controle de revisão:', error);
      throw error;
    }
    if (!data) {
      throw new Error('ASSESSMENT_RESULT_CONCURRENT_MODIFICATION');
    }

    return this.mapRecord(data as AssessmentResultV2);
  }

  static async countNonCancelledBySession(clientId: string, sessionId: string): Promise<number> {
    const { count, error } = await supabase
      .from('assessment_results_v2')
      .select('resultId', { count: 'exact', head: true })
      .eq('clientId', clientId)
      .eq('sessionId', sessionId)
      .neq('status', 'cancelled');

    if (error) {
      console.error('Erro ao contar resultados ativos da sessão V2:', error);
      throw error;
    }

    return count ?? 0;
  }

  static async listByIds(clientId: string, resultIds: string[]): Promise<AssessmentResultV2[]> {
    if (!resultIds.length) return [];

    const uniqueIds = [...new Set(resultIds)];
    const records: AssessmentResultV2[] = [];

    for (let index = 0; index < uniqueIds.length; index += this.MAX_IDS_PER_QUERY) {
      const batch = uniqueIds.slice(index, index + this.MAX_IDS_PER_QUERY);
      const { data, error } = await supabase
        .from('assessment_results_v2')
        .select('*')
        .eq('clientId', clientId)
        .in('resultId', batch);

      if (error) {
        console.error('Erro ao listar assessment_results_v2 por ids:', error);
        throw error;
      }

      records.push(...((data as AssessmentResultV2[] | null) ?? []));
    }

    const recordById = new Map(records.map((item) => [item.resultId, item]));
    return uniqueIds
      .map((resultId) => recordById.get(resultId))
      .filter((item): item is AssessmentResultV2 => Boolean(item))
      .map((item) => this.mapRecord(item));
  }

  private static async getById(resultId: string, clientId: string): Promise<AssessmentResultV2> {
    const { data, error } = await supabase
      .from('assessment_results_v2')
      .select('*')
      .eq('resultId', resultId)
      .eq('clientId', clientId)
      .single();

    if (error) {
      console.error('Erro ao buscar assessment_results_v2 por id:', error);
      throw error;
    }

    return this.mapRecord(data as AssessmentResultV2);
  }

  private static buildLifecycleAssetsJson(
    currentAssetsJson: Record<string, unknown> | null | undefined,
    entry: AssessmentResultLifecycleEntry,
  ): Record<string, unknown> {
    const assetsJson = currentAssetsJson && typeof currentAssetsJson === 'object' ? currentAssetsJson : {};
    const statusHistory = Array.isArray(assetsJson.statusHistory) ? assetsJson.statusHistory : [];
    const invalidationHistory = Array.isArray(assetsJson.invalidation_history) ? assetsJson.invalidation_history : [];
    const restorationHistory = Array.isArray(assetsJson.restoration_history) ? assetsJson.restoration_history : [];

    return {
      ...assetsJson,
      statusInfo: entry,
      statusHistory: [...statusHistory, entry],
      invalidation_info: entry.action === 'invalidated' ? entry : assetsJson.invalidation_info ?? null,
      invalidation_history:
        entry.action === 'invalidated' ? [...invalidationHistory, entry] : invalidationHistory,
      restoration_info: entry.action === 'restored' ? entry : assetsJson.restoration_info ?? null,
      restoration_history:
        entry.action === 'restored' ? [...restorationHistory, entry] : restorationHistory,
    };
  }

  private static async changeStatus(
    resultId: string,
    clientId: string,
    nextStatus: 'completed' | 'cancelled',
    action: AssessmentResultLifecycleAction,
    audit: Omit<AssessmentResultLifecycleEntry, 'action' | 'timestamp'> & { justification: string },
  ): Promise<AssessmentResultV2> {
    const current = await this.getById(resultId, clientId);
    const entry: AssessmentResultLifecycleEntry = {
      action,
      timestamp: new Date().toISOString(),
      userId: audit.userId,
      userName: audit.userName,
      userEmail: audit.userEmail,
      justification: audit.justification,
    };

    const nextAssetsJson = this.buildLifecycleAssetsJson(current.assetsJson, entry);

    const { data, error } = await supabase
      .from('assessment_results_v2')
      .update({
        status: nextStatus,
        assetsJson: nextAssetsJson,
        updatedAt: new Date().toISOString(),
      })
      .eq('resultId', resultId)
      .eq('clientId', clientId)
      .select('*')
      .single();

    if (error) {
      console.error('Erro ao alterar status de assessment_results_v2:', error);
      throw error;
    }

    return this.mapRecord(data as AssessmentResultV2);
  }

  static async setStatus(
    resultId: string,
    clientId: string,
    status: AssessmentResultV2['status'],
    placement?: Pick<AssessmentResultV2, 'groupIndex' | 'groupTotal'>,
  ): Promise<AssessmentResultV2> {
    const { data, error } = await supabase
      .from('assessment_results_v2')
      .update({
        status,
        ...(placement ? { groupIndex: placement.groupIndex, groupTotal: placement.groupTotal } : {}),
        updatedAt: new Date().toISOString(),
      })
      .eq('resultId', resultId)
      .eq('clientId', clientId)
      .select('*')
      .single();

    if (error) {
      console.error('Erro ao atualizar status simples de assessment_results_v2:', error);
      throw error;
    }

    return this.mapRecord(data as AssessmentResultV2);
  }

  static async delete(resultId: string, clientId: string): Promise<void> {
    const { error } = await supabase
      .from('assessment_results_v2')
      .delete()
      .eq('resultId', resultId)
      .eq('clientId', clientId);

    if (error) {
      console.error('Erro ao excluir assessment_results_v2:', error);
      throw error;
    }
  }

  static async invalidate(
    resultId: string,
    clientId: string,
    audit: Omit<AssessmentResultLifecycleEntry, 'action' | 'timestamp'> & { justification: string },
  ): Promise<AssessmentResultV2> {
    return this.changeStatus(resultId, clientId, 'cancelled', 'invalidated', audit);
  }

  static async restore(
    resultId: string,
    clientId: string,
    audit: Omit<AssessmentResultLifecycleEntry, 'action' | 'timestamp'> & { justification: string },
  ): Promise<AssessmentResultV2> {
    return this.changeStatus(resultId, clientId, 'completed', 'restored', audit);
  }
}
